import { toFixedClean, getOutputFrequencyAmplitude } from '../../utils';
import FilterService from '../../services/filter';
import type { FilterResult } from '../../services/filter';
import type { HarmonicSequenceValue } from './harmonics';
/**
 * Model for initial data for the harmonic table
 *
 * The spur output has three tables. Every table has a multiplier of 16
 * and produces a total of 33 rows.
 *
 * 2 for each value of the multiplier (0 - 15). Total of 32
 * The remaining row is the initial, represented by this object.
 *
 */
export default class HarmonicsInitialModel implements HarmonicSequenceValue {
  multiplierIndex = 0;
  harmonicsSequence: number;
  targetFrequency: number;
  systemClock: number;
  filter?: FilterService;
  harmonicsSpurLevel: number;
  pptSpurLevel?: number;

  constructor(
    multiplierIndex: number,
    harmonicsSequence: number,
    systemClock: number,
    targetFrequency: number,
    harmonicsSpurLevel: number,
    pptSpurLevel: number,
    filter?: FilterService,
  ) {
    this.multiplierIndex = multiplierIndex;
    this.harmonicsSequence = harmonicsSequence;
    this.systemClock = systemClock;
    this.targetFrequency = targetFrequency;
    this.harmonicsSpurLevel = harmonicsSpurLevel;
    this.pptSpurLevel = pptSpurLevel;
    if (filter) {
      this.filter = filter;
    }
  }

  /**
   * Computes the sequence name based on the harmonics sequence
   */
  get sequenceName(): string {
    return (this.harmonicsSequence <= 1 ? '' : this.harmonicsSequence.toString()) + 'fOut';
  }

  /**
   * Sanitizes the value of _seqFrequency
   */
  get sequenceFrequency(): string {
    return toFixedClean(this._seqFrequency, 0);
  }

  /**
   * Santizes the value of seqAmplitude
   */
  get sequenceAmplitude(): string {
    const filterAmplitude = this.filterValues.filterAmplitude;
    if (this._seqFrequency !== 0) {
      return filterAmplitude ? toFixedClean(this._seqAmplitude, 6) : toFixedClean(this._seqAmplitude, 7);
    }
    return this._seqAmplitude.toString();
  }

  /**
   * Computes the sequence attenuation based on the system clock value and the output frequency
   */
  get sequenceAttenuation(): number {
    return getOutputFrequencyAmplitude(this.systemClock, this.targetFrequency);
  }

  /**
   * Computes the sequence frequency value
   */
  get _seqFrequency(): number {
    return this.harmonicsSequence * this.targetFrequency;
  }

  /**
   * Computes the sequence amplitude value.
   * Triggers a recompute when the any of the following dependencies change.
   * This might look weird |parseFloat(toFixedClean(result, 6))|,
   * but this was done to match the values in this version
   * with the old version .
   * @see {'module:models/clock'}
   */

  get _seqAmplitude(): number {
    const filterAmplitude = this.filterValues.filterAmplitude;
    let seqAmplitude = this.getOutputFrequencyAmplitudeDBC(this.systemClock, this._seqFrequency);
    seqAmplitude = this.harmonicsSequence > 1 ? seqAmplitude + this.harmonicsSpurLevel : seqAmplitude;
    return filterAmplitude ? seqAmplitude + filterAmplitude : seqAmplitude;
  }

  /**
   * Computes for the Output frequency amplitude dbc
   * @function
   * @returns {float}
   * @private
   */
  getOutputFrequencyAmplitudeDBC(systemClock: number, targetFrequency: number): number {
    const carrierAmplitude = getOutputFrequencyAmplitude(systemClock, targetFrequency);
    return isNaN(carrierAmplitude) ? 0 : 20 * (Math.log(carrierAmplitude) / Math.log(10));
  }

  /**
   * Sets the filter attenuation and filter amplitude values
   * @function
   * @private
   */
  get filterValues(): FilterResult {
    return this.filter
      ? this.filter.applyFilter(this._seqFrequency)
      : { filterAmplitude: null, filterAttenuation: null };
  }
}
